import React, { memo, useCallback, useRef, useState } from 'react';
import { useToggle } from 'ui-components/hooks/use-toggle';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { Button } from 'ui-components/components/button';
import { testId } from 'ui-components/services/testid-service';

import ActiveStoreItem from './active-store-item';
import style from './store-status-dropdown.module.less';
import { useInventorySyncOnImportComplete } from './use-inventory-sync';
import { MarketplaceIds } from '../../../enums/marketplace-ids';
import Global from '../../../store/global-selectors';
import { getStoreErrorLogData, refreshAllStores, refreshStore } from '../../../store/seller/seller.actions';
import { importOrdersCoachmarkClassname } from '../../coachmark/classname-targets';
import UpdateStoresHotkey from '../../hotkeys/advanced-hotkeys/update-stores';
import { StoreErrorLogsModalV2Component } from '../../modals/store-error-logs-modal-v2';
import { StoreDropdownRefreshIcon } from '../icons/store-dropdown-refresh-icon';
import { StoreRefreshIcon } from '../icons/store-refresh-icon';
import messages from '../messages';

const coachmarkClassnames = {
  importOrders: importOrdersCoachmarkClassname,
};

const renderUpdateAllButton = (stores, refresh) => {
  const activeStores = stores.filter((x) => x.isActive && x.marketplaceId !== MarketplaceIds.ShipStation).length;
  return (
    <div className={style['gotta-update-em-all']}>
      <span>
        <FormattedMessage {...messages.storeCount} values={{ activeStores }} />
      </span>
      <Button className={style['update-all-btn']} onClick={refresh} theme='secondary'>
        <StoreDropdownRefreshIcon className={style['update-all-icon']} />
        <FormattedMessage {...messages.updateAllStores} />
      </Button>
    </div>
  );
};

const activeStoreSelector = createSelector(
  (stores) => stores,
  (stores) => stores.filter((x) => x.marketplaceId !== MarketplaceIds.ShipStation && x.isActive),
);

const StoreStatusDropdown = () => {
  const dispatch = useDispatch();
  const stores = useSelector(Global.selectSellerStores);
  const storeErrorLogs = useSelector(Global.selectSellerStoreErrorLogs);
  const isStoreUpdating = useSelector(Global.selectSellerIsLoading);
  const loadingStoreIds = useSelector(Global.selectSellerLoadingStoreIds);
  const startListeningForImportComplete = useInventorySyncOnImportComplete();

  const containerRef = useRef(null);
  const { isActivated: isDropdownOpen, activate: openDropdown, deactivate: closeDropdown } = useToggle(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  const refreshStoreCallback = useCallback(
    (storeGuid) => {
      dispatch(refreshStore(storeGuid));
      startListeningForImportComplete();
    },
    [dispatch, startListeningForImportComplete],
  );
  const refreshAllStoresCallback = useCallback(() => {
    dispatch(refreshAllStores());
    startListeningForImportComplete();
  }, [dispatch, startListeningForImportComplete]);

  const [displayStoreErrorModal, setDisplayStoreErrorModal] = useState(false);
  const [currentStoreGuid, setCurrentStoreGuid] = useState(null);

  const handleErrorModalVisibility = useCallback(
    (storeGuid) => {
      if (displayStoreErrorModal) {
        setDisplayStoreErrorModal(false);
        setCurrentStoreGuid(null);
        return;
      }
      setDisplayStoreErrorModal(true);
      setCurrentStoreGuid(storeGuid);
      dispatch(getStoreErrorLogData(storeGuid));
    },
    [dispatch, displayStoreErrorModal, setDisplayStoreErrorModal],
  );

  const handleMouseEnter = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        right: window.innerWidth - rect.right,
      });
    }
    openDropdown();
  }, [openDropdown]);

  const handleMouseLeave = useCallback(() => {
    closeDropdown();
  }, [closeDropdown]);

  const activeStores = activeStoreSelector(stores);
  const errorCount = activeStores.filter((store) => store.refreshStatus === 'Error').length;

  const dropdownContent = isDropdownOpen
    ? ReactDOM.createPortal(
        <div
          className={style['content-container-portal']}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={{ top: dropdownPosition.top, right: dropdownPosition.right }}
        >
          <div className={style['store-list-wrapper']}>
            {renderUpdateAllButton(stores, refreshAllStoresCallback)}
            {activeStores.map((store) => (
              <ActiveStoreItem
                key={store.storeGuid}
                handleErrorModalVisibility={handleErrorModalVisibility}
                isStoreLoading={loadingStoreIds.indexOf(store.storeGuid) >= 0}
                refreshStore={refreshStoreCallback}
                store={store}
              />
            ))}
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <UpdateStoresHotkey>
      <div
        ref={containerRef}
        className={style['store-dropdown-container']}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...testId('store-dropdown-container')}
        tabIndex={-1}
      >
        {displayStoreErrorModal ? (
          <StoreErrorLogsModalV2Component
            onModalClose={handleErrorModalVisibility}
            storeErrorLogs={storeErrorLogs}
            storeGuid={currentStoreGuid}
          />
        ) : null}
        <div className={classnames(style['icon-wrapper'], { [style.rotating]: isStoreUpdating }, coachmarkClassnames.importOrders)}>
          <StoreRefreshIcon className={style['refresh-icon']} decorationClassName={style['alert-icon']} showDecoration={errorCount > 0} />
        </div>
        {dropdownContent}
      </div>
    </UpdateStoresHotkey>
  );
};

export default memo(StoreStatusDropdown);
